import {
  getAllParticipants,
  getLayout,
  getMainParticipant,
  getMainParticipantFromState,
  useBaseStore,
  useConferenceStore,
  useParticipantStore,
} from '../store';
import { debounce, VIDEO_QUALITY_LEVELS } from '../utils';

type Constraints = {
  constraints: Record<string, { maxHeight: number }>;
  defaultConstraints: { maxHeight: number };
  lastN: number;
  onStageSources: string[];
  selectedSources: string[];
};

function getNearestVideoQualityLevel(height: number): number {
  if (height <= VIDEO_QUALITY_LEVELS.LOW) {
    return VIDEO_QUALITY_LEVELS.LOW;
  } else if (height <= VIDEO_QUALITY_LEVELS.STANDARD) {
    return VIDEO_QUALITY_LEVELS.STANDARD;
  }
  return VIDEO_QUALITY_LEVELS.HIGH;
}

function setReceiverConstraintsInternal() {
  const constraints: Constraints = {
    constraints: {},
    defaultConstraints: { maxHeight: VIDEO_QUALITY_LEVELS.NONE },
    lastN: -1,
    onStageSources: [],
    selectedSources: [],
  };
  const layout = getLayout();
  if (layout === 'SPOTLIGHT') {
    const mainParticipantSources = getMainParticipant().sources?.get('video');
    if (mainParticipantSources) {
      Array.from(mainParticipantSources.keys()).forEach(source => {
        constraints.constraints[source] = {
          maxHeight: 720,
        };
        constraints.onStageSources.push(source);
      });
    }
  } else if (layout === 'TILE') {
    const tileHeight = getNearestVideoQualityLevel(
      useBaseStore.getState().tileDimensions.tileHeight,
    );
    const visibleParticipants = useBaseStore.getState().visibleParticipants;
    const participants = getAllParticipants().slice(
      visibleParticipants.startIndex,
      visibleParticipants.endIndex + 1,
    );

    participants.forEach(participant => {
      const isLocal = participant.isLocal;
      const videoSources = participant.sources?.get('video');
      if (!isLocal && videoSources) {
        Array.from(videoSources.keys()).forEach(source => {
          constraints.constraints[source] = {
            maxHeight: tileHeight,
          };
        });
      }
    });
  } else if (layout === 'SIDEBAR') {
    const participants = useParticipantStore.getState().participants;
    participants.forEach(participant => {
      const videoSources = participant.sources?.get('video');
      if (videoSources) {
        Array.from(videoSources.keys()).forEach(source => {
          constraints.constraints[source] = {
            maxHeight: 180,
          };
        });
      }
    });

    const mainParticipant = getMainParticipant();
    const videoSources = mainParticipant.sources?.get('video');
    if (videoSources) {
      Array.from(videoSources.keys()).forEach(source => {
        constraints.constraints[source] = {
          maxHeight: 720,
        };
        constraints.onStageSources.push(source);
      });
    }
  }

  const conference = useConferenceStore.getState().conference;
  if (conference && Object.keys(constraints.constraints).length > 0) {
    conference.setReceiverConstraints(constraints);
  }
}

// Debounced version of setReceiverConstraints with 100ms delay
export const setReceiverConstraints = debounce(
  setReceiverConstraintsInternal,
  200,
);

useBaseStore.subscribe(
  state => state.visibleParticipants,
  () => {
    setReceiverConstraints();
  },
);

useBaseStore.subscribe(
  state => state.layout,
  () => {
    setReceiverConstraints();
  },
);

useParticipantStore.subscribe(
  state => getMainParticipantFromState(state).pid,
  () => {
    const layout = useBaseStore.getState().layout;
    if (layout === 'SPOTLIGHT' || layout === 'SIDEBAR') {
      setReceiverConstraints();
    }
  },
);
